import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useAuthStore } from '@/core/stores/auth.store';
import { useNotificationHubContext } from '@/features/notifications/public';
import { TASK_KEYS } from './queryKeys';
import { updateTaskEntity, removeTaskEntity } from './useTaskEntityCache';
import type {
  TaskCreatedPayload,
  TaskUpdatedPayload,
  TaskDeletedPayload,
  TaskMovedPayload,
  TaskAssignedPayload,
  TaskPriorityChangedPayload,
  TaskToggledPayload,
} from '../types/realtime.types';
import type { TaskPriority } from '../types';

/**
 * Subscribes to real-time board events for a given boardId.
 *
 * Uses the shared SignalR connection from NotificationHubContext.
 * Calls SubscribeToBoard on mount and UnsubscribeFromBoard on unmount.
 * Applies cache updates via updateTaskEntity / removeTaskEntity so all views
 * (board, list, calendar) reflect changes immediately.
 *
 * Self-mutation skip: if actorId === currentUserId the change was applied
 * optimistically by the mutation — the cache update is skipped to avoid flicker.
 */
export function useBoardRealtimeEvents(projectId: string, boardId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';
  const { connectionRef, reconnectCount } = useNotificationHubContext();

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || !boardId || !orgId) return;

    const currentUserId = useAuthStore.getState().user?.id;

    // ── Helpers ─────────────────────────────────────────────────────────────

    const isSelf = (actorId: string) => actorId === currentUserId;

    /** Maps the raw priority string from the backend to the TaskPriority union. */
    function normalizePriority(raw: string): TaskPriority | undefined {
      const map: Record<string, TaskPriority> = {
        Low: 'low',
        Medium: 'medium',
        High: 'high',
        Urgent: 'urgent',
      };
      return map[raw];
    }

    // ── Handlers ────────────────────────────────────────────────────────────

    const onTaskCreated = (_p: TaskCreatedPayload) => {
      // Thin payload — not enough to reconstruct a full Task; invalidate instead.
      queryClient.invalidateQueries({
        queryKey: TASK_KEYS.projectRoot(orgId, projectId),
      });
    };

    const onTaskUpdated = (p: TaskUpdatedPayload) => {
      if (isSelf(p.actorId)) return;
      updateTaskEntity(queryClient, orgId, p.taskId, (t) => ({ ...t, title: p.title }));
    };

    const onTaskDeleted = (p: TaskDeletedPayload) => {
      if (isSelf(p.actorId)) return;
      removeTaskEntity(queryClient, orgId, p.taskId);
    };

    const onTaskMoved = (p: TaskMovedPayload) => {
      if (isSelf(p.actorId)) return;
      updateTaskEntity(queryClient, orgId, p.taskId, (t) => ({
        ...t,
        status: { ...t.status, id: p.newStatusId },
        groupId: p.newGroupId ?? t.groupId,
      }));
      // Invalidate board to restore correct column ordering after the move.
      queryClient.invalidateQueries({
        queryKey: TASK_KEYS.projectRoot(orgId, projectId),
      });
    };

    const onTaskAssigned = (p: TaskAssignedPayload) => {
      if (isSelf(p.actorId)) return;
      updateTaskEntity(queryClient, orgId, p.taskId, (t) => ({
        ...t,
        assignee: p.assigneeId ? { id: p.assigneeId, name: '—', color: '' } : null,
      }));
    };

    const onTaskPriorityChanged = (p: TaskPriorityChangedPayload) => {
      if (isSelf(p.actorId)) return;
      const priority = normalizePriority(p.priority);
      updateTaskEntity(queryClient, orgId, p.taskId, (t) => ({
        ...t,
        priority,
      }));
    };

    const onTaskToggled = (p: TaskToggledPayload) => {
      if (isSelf(p.actorId)) return;
      updateTaskEntity(queryClient, orgId, p.taskId, (t) => ({
        ...t,
        isCompleted: p.isCompleted,
      }));
    };

    // ── Register listeners ───────────────────────────────────────────────────

    connection.on('ReceiveTaskCreated', onTaskCreated);
    connection.on('ReceiveTaskUpdated', onTaskUpdated);
    connection.on('ReceiveTaskDeleted', onTaskDeleted);
    connection.on('ReceiveTaskMoved', onTaskMoved);
    connection.on('ReceiveTaskAssigned', onTaskAssigned);
    connection.on('ReceiveTaskPriorityChanged', onTaskPriorityChanged);
    connection.on('ReceiveTaskToggled', onTaskToggled);

    connection.invoke('SubscribeToBoard', boardId).catch((err: unknown) => {
      console.warn('[useBoardRealtimeEvents] SubscribeToBoard failed:', err);
    });

    return () => {
      connection.invoke('UnsubscribeFromBoard', boardId).catch(() => {});
      connection.off('ReceiveTaskCreated', onTaskCreated);
      connection.off('ReceiveTaskUpdated', onTaskUpdated);
      connection.off('ReceiveTaskDeleted', onTaskDeleted);
      connection.off('ReceiveTaskMoved', onTaskMoved);
      connection.off('ReceiveTaskAssigned', onTaskAssigned);
      connection.off('ReceiveTaskPriorityChanged', onTaskPriorityChanged);
      connection.off('ReceiveTaskToggled', onTaskToggled);
    };
    // reconnectCount drives re-subscription after reconnect — SignalR lifecycle callbacks
    // cannot be unregistered, so this counter in deps replaces a per-effect callback.
  }, [connectionRef, boardId, projectId, orgId, queryClient, reconnectCount]);
}
